import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { getQuotationRequests } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';

export const QuotationRequestsPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [sortBy, setSortBy] = useState<'createdAt' | 'reference' | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getQuotationRequests(
        variables.cursor,
        variables.take,
        {
          search: searchValue,
        },
        sortBy,
        sortOrder,
      );
    },
    [searchValue, sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue, sortBy, sortOrder]);

  const tableHeaders: ITableHeader[] = [
    {
      id: 'customer',
      name: 'Klant',
    },
    {
      id: 'reference',
      name: 'Referentie',
      sortable: true,
    },
    {
      id: 'quote',
      name: 'Offerte',
    },
    {
      id: 'createdAt',
      name: 'Aangevraagd op',
      sortable: true,
    },
  ];

  const title = 'Offerte aanvragen';
  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={<PageHeading noMarginBottom leftSide={<Breadcrumb currentItem={title} />} />}
        panel
        filters={
          <Input
            type="text"
            placeholder="Zoek naar een aanvraag..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as 'createdAt' | 'reference' | undefined);
            setSortOrder(order);
          }}
          mapData={(request) => {
            return [
              <Link to={`/internal/customers/${request.customer.id}/general`} className="link-text">
                {request.customer.name}
              </Link>,
              <Link to={`${request.id}`} className="link-text">
                {request.reference ?? 'DRAFT'}
              </Link>,
              request.quotationId ? (
                <Link to={`../quotations/${request.quotationId}`}>{request.reference}</Link>
              ) : (
                'n/a'
              ),
              formatDate(request.createdAt),
            ];
          }}
        />
      </ListPage>
    </>
  );
};
